/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box } from '@mui/material';
import { FC, memo, ReactNode, useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ErrorTriangleIcon from '../../../../shared/icons/icons-revamp/error_triangle';
import QSTooltip from '../../../../shared/components/molecules/tooltip';
import CallRestrictionInfoModal from '../../../../shared/components/molecules/call-restriction-info-modal';
import { WabaItem, formatWabaNames } from './util';

interface RestrictedWabasIconProps {
  restrictedWabas: WabaItem[];
}

const RestrictedWabasIcon: FC<RestrictedWabasIconProps> = memo(({ restrictedWabas }) => (
  <QSTooltip
    title={`WhatsApp calling on ${formatWabaNames(
      restrictedWabas
    )} has been paused by Meta for up to 7 days due to low pickup rates and users reporting or blocking your calls.`}
    placement="bottom"
    arrow
  >
    <Box
      css={css`
        display: flex;
        align-items: center;
      `}
    >
      <ErrorOutlineIcon
        css={css`
          font-size: 16px;
          color: #d93025;
        `}
      />
    </Box>
  </QSTooltip>
));

interface ViolatedWabasIconProps {
  violatedWabas: WabaItem[];
}

const ViolatedWabasIcon: FC<ViolatedWabasIconProps> = memo(({ violatedWabas }) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <QSTooltip
        title={
          <>
            {`Meta may temporarily pause outgoing calling on ${formatWabaNames(violatedWabas)} for 7 days. `}
            <Box
              component="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setModalOpen(true);
              }}
              css={css`
                background: none;
                border: none;
                padding: 0;
                color: inherit;
                cursor: pointer;
                font: inherit;
                text-decoration: underline;
              `}
            >
              Learn more
            </Box>
          </>
        }
        placement="bottom"
        arrow
      >
        <Box
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <ErrorTriangleIcon size={16} />
        </Box>
      </QSTooltip>

      <Box onClick={(e) => e.stopPropagation()}>
        <CallRestrictionInfoModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          channelName={violatedWabas.map((w) => w.displayName)}
        />
      </Box>
    </>
  );
});

export const getCallsTrailingIcon = (restrictedWabas: WabaItem[], violatedWabas: WabaItem[]): ReactNode => {
  const hasRestricted = restrictedWabas.length > 0;
  const hasViolated = violatedWabas.length > 0;

  if (!hasRestricted && !hasViolated) return undefined;

  return (
    <Box
      css={css`
        display: flex;
        align-items: center;
        gap: 4px;
      `}
    >
      {hasRestricted && <RestrictedWabasIcon restrictedWabas={restrictedWabas} />}
      {hasViolated && <ViolatedWabasIcon violatedWabas={violatedWabas} />}
    </Box>
  );
};
